import SEO from "@/components/SEO";
import { BUSINESS } from "@/lib/constants";

export default function Privacy() {
  return (
    <div data-testid="page-privacy">
      <SEO
        title="Privacy Policy — Carfornia Buyers"
        description="How Carfornia Buyers collects, uses, stores and protects your personal information when you request an offer or contact us."
        canonical="https://carforniabuyers.com/privacy"
      />
      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Legal</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mt-3">Privacy Policy</h1>
          <p className="text-slate-500 mt-3 text-sm">Last updated: October 2026</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-7 text-slate-700 leading-relaxed">
          <p>{BUSINESS.name} (“we,” “our,” or “us”) operates carforniabuyers.com. This Privacy Policy explains what personal information we collect when you use the site, request an offer for your vehicle, or contact us, and how we use and protect it. We are a small, independently owned business located at {BUSINESS.address}.</p>

          <Section title="Information you give us">
            <p>When you request an offer we collect the information you enter in the form: your name, email address, phone number and ZIP code, and details about your vehicle such as VIN, license plate and state, year, make, model, trim, mileage, condition, title status, accident history and any notes you add. When you use the contact form we collect your name, email, phone (optional), subject and message.</p>
          </Section>

          <Section title="Information collected automatically">
            <p>Our hosting provider’s servers keep standard access logs (IP address, browser type, pages requested, timestamps) to keep the site running and secure. We do not currently run third-party advertising or analytics trackers on this site.</p>
          </Section>

          <Section title="How we use your information">
            <p>We use the information you provide to review your request, contact you by phone, text or email about your vehicle, arrange an inspection, prepare a written offer, complete the purchase and title paperwork if you accept, keep required business and transaction records, and respond to your messages. We do not sell your personal information and we do not share it with other dealers or lead-buying companies.</p>
          </Section>

          <Section title="Where your information goes">
            <p>Submissions are stored in a private database operated for us by our hosting provider and are protected by access controls so they are not publicly readable. Each submission is also emailed to our business inbox through a transactional email provider (Resend). If you enter a VIN, the VIN alone is sent to the U.S. National Highway Traffic Safety Administration’s public VIN decoder to identify the year, make and model; no personal details are sent with it. We may share information with our bank or payment provider to pay you, with the California DMV to complete a title transfer, and with authorities when required by law.</p>
          </Section>

          <Section title="How long we keep it">
            <p>We keep offer requests and messages for as long as needed to follow up with you and to meet our record-keeping obligations for completed vehicle purchases. You may ask us to delete a request that did not lead to a sale at any time.</p>
          </Section>

          <Section title="Security">
            <p>The site is served over HTTPS, so information you submit is encrypted in transit. Stored records are restricted to the business owner. No method of transmission or storage is completely secure, but we take reasonable steps to protect your information and will notify you if we become aware of a breach affecting your data.</p>
          </Section>

          <Section title="Your California privacy rights">
            <p>California residents may request to know what personal information we hold about them, ask us to correct or delete it, and will not be treated differently for exercising these rights. To make a request, call or text {BUSINESS.phone}, use the contact form, or write to us at {BUSINESS.address}. We will verify your identity before acting on a request.</p>
          </Section>

          <Section title="Cookies">
            <p>The site uses only the technical cookies and local storage needed for the pages to function. We do not use cookies to track you across other websites.</p>
          </Section>

          <Section title="Children">
            <p>Our services are for adults 18 and older. We do not knowingly collect information from minors.</p>
          </Section>

          <Section title="Changes and contact">
            <p>If we change this policy we will update the date at the top of this page. Questions? Call or text {BUSINESS.phone}, use the contact form, or write to {BUSINESS.name}, {BUSINESS.address}.</p>
          </Section>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-xl font-bold text-slate-900 mt-8 mb-3">{title}</h2>
      {children}
    </div>
  );
}
